import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react'
import type Anthropic from '@anthropic-ai/sdk'
import { ask, pastQuestions, readTables, saveQuestion, type Turn } from './lib/ask'
import type { Database } from './lib/google/sheets'
import type { Meter } from './lib/usage'

const EXAMPLES = [
  'Should I pay off my car loan early?',
  'How much did I spend on dining in the last three months?',
  'Is my emergency fund big enough?',
  'What should I do with the cash in my HSA?',
]

interface Props {
  anthropic: Anthropic
  db: Database
  /** Text from an "Ask about this" button; `n` changes on every press so the same text can be sent twice. */
  draft?: { text: string; n: number }
  meter?: Meter
}

export function Ask({ anthropic, db, draft, meter }: Props) {
  const [question, setQuestion] = useState('')
  const box = useRef<HTMLTextAreaElement>(null)
  const section = useRef<HTMLElement>(null)

  // Filled in, not sent: the person finishes the question and the call is theirs to make.
  useEffect(() => {
    if (!draft) return
    setQuestion(draft.text)
    // After the dashboard has switched to the tab holding the question box.
    const t = setTimeout(() => {
      section.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      const el = box.current
      if (el) {
        el.focus({ preventScroll: true })
        el.setSelectionRange(draft.text.length, draft.text.length)
      }
    }, 50)
    return () => clearTimeout(t)
  }, [draft])
  const [thread, setThread] = useState<Turn[]>([])
  const [pending, setPending] = useState<{ question: string; answer: string; notes: string[] }>()
  const [error, setError] = useState<string>()
  const [past, setPast] = useState<(Turn & { asked_at: string })[]>([])

  useEffect(() => {
    pastQuestions(db).then(setPast, () => setPast([]))
  }, [db])

  const submit = async (text: string) => {
    const q = text.trim()
    if (!q || pending) return
    setQuestion('')
    setError(undefined)
    setPending({ question: q, answer: '', notes: [] })
    try {
      const tables = await readTables(db)
      const answer = await ask(
        anthropic,
        tables,
        q,
        thread,
        (delta) => setPending((p) => p && { ...p, answer: p.answer + delta }),
        (note) => setPending((p) => p && { ...p, notes: [...p.notes, note] }),
        meter,
      )
      const turn = { question: q, answer }
      setThread((t) => [...t, turn])
      setPending(undefined)
      saveQuestion(db, turn)
        .then(() => setPast((p) => [{ ...turn, asked_at: 'just now' }, ...p]))
        .catch(() => setError('Answered, but could not save it to the Questions tab.'))
    } catch (e) {
      setPending(undefined)
      setQuestion(q)
      setError((e as Error).message)
    }
  }

  return (
    <section className="section ask" ref={section}>
      <div className="row">
        <h2>Ask about your finances</h2>
        {thread.length > 0 && !pending && (
          <button className="quiet" onClick={() => setThread([])}>New conversation</button>
        )}
      </div>

      {thread.map((t, i) => (
        <Exchange key={i} question={t.question} answer={t.answer} />
      ))}
      {pending && <Exchange question={pending.question} answer={pending.answer} notes={pending.notes} live />}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          submit(question)
        }}
      >
        <textarea
          ref={box}
          rows={draft ? 3 : 2}
          value={question}
          disabled={!!pending}
          placeholder={thread.length ? 'Ask a follow-up…' : 'Ask anything, e.g. “Should I pay off my car loan early?”'}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              submit(question)
            }
          }}
        />
        <button type="submit" disabled={!question.trim() || !!pending}>{pending ? 'Answering…' : 'Ask'}</button>
      </form>
      {error && <p className="error">✕ {error}</p>}

      {thread.length === 0 && !pending && (
        <div className="examples">
          {EXAMPLES.map((ex) => (
            <button key={ex} className="quiet" onClick={() => submit(ex)}>{ex}</button>
          ))}
        </div>
      )}

      {past.length > 0 && (
        <details className="past">
          <summary>{past.length} earlier question{past.length === 1 ? '' : 's'}</summary>
          {past.map((p, i) => (
            <details key={i} className="past-item">
              <summary><span className="label">{p.asked_at}</span> {p.question}</summary>
              <div className="answer"><Markdown text={p.answer} /></div>
            </details>
          ))}
        </details>
      )}
      <p className="label disclaimer">Answers come from Claude using your statements. Not personalised investment, tax or legal advice.</p>
    </section>
  )
}

function Exchange({ question, answer, notes = [], live }: { question: string; answer: string; notes?: string[]; live?: boolean }) {
  return (
    <div className="exchange">
      <p className="q">{question}</p>
      {live && notes.length > 0 && (
        <ul className="notes">
          {notes.map((n, i) => (
            <li key={i}>↳ {n}</li>
          ))}
        </ul>
      )}
      <div className="answer">
        {answer ? <Markdown text={answer} /> : live && <p className="muted">Thinking…</p>}
      </div>
    </div>
  )
}

/** The small subset of markdown the prompt asks for: paragraphs, "- " lists, "### " headings, **bold**. */
export function Markdown({ text }: { text: string }) {
  const blocks: ReactNode[] = []
  let list: string[] = []
  const flush = () => {
    if (list.length) blocks.push(<ul key={blocks.length}>{list.map((l, i) => <li key={i}>{inline(l)}</li>)}</ul>)
    list = []
  }
  for (const para of text.split(/\n{2,}/)) {
    for (const line of para.split('\n')) {
      const bullet = /^\s*[-*•]\s+(.*)$/.exec(line)
      if (bullet) {
        list.push(bullet[1])
        continue
      }
      flush()
      const heading = /^#{1,4}\s+(.*)$/.exec(line)
      if (heading) blocks.push(<h4 key={blocks.length}>{inline(heading[1])}</h4>)
      else if (line.trim()) blocks.push(<p key={blocks.length}>{inline(line)}</p>)
    }
    flush()
  }
  return <>{blocks}</>
}

function inline(s: string): ReactNode {
  return s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <b key={i}>{part.slice(2, -2)}</b> : <Fragment key={i}>{part}</Fragment>,
  )
}
